import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Eye, Loader2, RefreshCw, XCircle, FileSearch } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const FILTRES = [
  ['toutes', 'Toutes', null, 'bg-[#0a3d62] text-white'],
  ['verifiees', 'Vérifiées', CheckCircle2, 'bg-emerald-600 text-white'],
  ['a_verifier', 'À vérifier', Clock, 'bg-amber-500 text-white'],
  ['refusees', 'Refusées', XCircle, 'bg-rose-600 text-white'],
];
const STATUT = {
  enregistre: ['Vérifiée — paiement enregistré', 'bg-emerald-100 text-emerald-800'],
  doublon: ['Vérifiée — déjà enregistrée', 'bg-emerald-100 text-emerald-800'],
  a_verifier: ['À vérifier par l\'administration', 'bg-amber-100 text-amber-800'],
  analyse: ['Vérification Klarys en cours', 'bg-sky-100 text-sky-800'],
  rejetee: ['Refusée', 'bg-rose-100 text-rose-800'],
};

export default function CooperPreuvesVirement() {
  const [filtre, setFiltre] = useState('toutes');
  const [data, setData] = useState(null);
  const charger = useCallback(async () => {
    setData(null);
    try { setData((await axios.get(`${API}/cooper/preuves-virement?statut=${filtre}`, h())).data); } catch { setData({ preuves: [], compteurs: {} }); }
  }, [filtre]);
  useEffect(() => { charger(); }, [charger]);
  const voir = async (p) => {
    try {
      const r = await axios.get(`${API}/preuves-virement/${p.id}/fichier`, { ...h(), responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Preuve indisponible'); }
  };
  const c = data?.compteurs || {};
  return (
    <div className="space-y-5" data-testid="cooper-preuves-virement">
      <div className="flex items-center gap-3">
        <FileSearch className="h-6 w-6 text-[#0a3d62]" />
        <div className="flex-1">
          <h2 className="text-xl font-bold text-[#0a3d62]">Preuves de virement de mes membres</h2>
          <p className="text-sm text-slate-500">Ordres de virement déposés, contrôlés par Klarys puis, si besoin, par l&apos;administration.</p>
        </div>
        <button type="button" onClick={charger} className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50" data-testid="cooper-preuves-rafraichir"><RefreshCw className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {FILTRES.slice(1).map(([k, l, Icon, cls]) => (
          <div key={k} className={`rounded-xl p-4 ${cls}`} data-testid={`cooper-preuves-compteur-${k}`}>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase opacity-90"><Icon className="h-4 w-4" />{l}</p>
            <p className="mt-1 text-3xl font-black">{c[k] ?? '—'}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTRES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFiltre(k)} data-testid={`cooper-preuves-filtre-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filtre === k ? 'bg-[#0a3d62] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{l}</button>
        ))}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : data.preuves.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Aucune preuve de virement pour ce filtre.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="p-3">Membre</th><th className="p-3">Réf. Solidaire</th><th className="p-3">Déposée le</th><th className="p-3">Montant lu</th><th className="p-3">Statut</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {data.preuves.map((p) => {
                const [lib, cls] = STATUT[p.statut] || STATUT.analyse;
                return (
                  <tr key={p.id} className="border-t border-slate-100 align-top" data-testid={`cooper-preuve-${p.id}`}>
                    <td className="p-3 font-semibold text-slate-800">{p.raison_sociale || '—'}</td>
                    <td className="p-3 font-mono text-xs text-slate-600">{p.numero_police}</td>
                    <td className="p-3 text-slate-600">{new Date(p.at).toLocaleDateString('fr-FR')}</td>
                    <td className="p-3">{p.lecture?.montant ? eur(p.lecture.montant) : '—'}</td>
                    <td className="p-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`cooper-preuve-statut-${p.id}`}>{lib}</span>
                      {p.statut === 'a_verifier' && (p.motifs || []).length > 0 && <p className="mt-1 text-xs text-amber-800">{p.motifs.join(' · ')}</p>}
                      {p.statut === 'rejetee' && p.motif_rejet && <p className="mt-1 text-xs text-rose-700">Motif : {p.motif_rejet}</p>}
                    </td>
                    <td className="p-3"><button type="button" onClick={() => voir(p)} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50" data-testid={`cooper-preuve-voir-${p.id}`}><Eye className="h-3.5 w-3.5" /> Voir</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
